// src/pages/suppliers/components/Footer.jsx
import React from 'react';
import { Box, Typography, Link, alpha, Tooltip } from '@mui/material';
import { BRAND_COLORS } from '../../buyers/components/styles';
import { OpenInNew as OpenInNewIcon } from '@mui/icons-material';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <Box
      component="footer"
      sx={{
        borderTop: '1px solid rgba(0,0,0,0.04)',
        bgcolor: 'rgba(255, 255, 255, 0.92)',
        backdropFilter: 'blur(10px)',
        px: { xs: 2, sm: 3 },
        py: 1.5,
        mt: 'auto',
        transition: 'all 0.3s ease',
        '&:hover': {
          bgcolor: '#FFFFFF',
          borderTopColor: alpha(BRAND_COLORS.primary, 0.1),
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 1,
          maxWidth: '100%',
        }}
      >
        {/* Left Section - Version & Brand */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Typography
            variant="caption"
            sx={{
              fontWeight: 500,
              color: '#9CA3AF',
              fontSize: '0.7rem',
              letterSpacing: '0.3px',
              fontFamily: '"Inter", "Segoe UI", system-ui, -apple-system, sans-serif',
            }}
          >
            v2.4.0
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: '#D1D5DB',
              fontSize: '0.7rem',
            }}
          >
            |
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: '#6B7280',
              fontWeight: 600,
              fontSize: '0.7rem',
              letterSpacing: '0.3px',
              fontFamily: '"Inter", "Segoe UI", system-ui, -apple-system, sans-serif',
            }}
          >
            ChemMart
          </Typography>
        </Box>

        {/* Center Section - Copyright */}
        <Typography
          variant="caption"
          sx={{
            color: '#9CA3AF',
            fontSize: '0.65rem',
            fontWeight: 400,
            letterSpacing: '0.2px',
            display: { xs: 'none', sm: 'block' },
            fontFamily: '"Inter", "Segoe UI", system-ui, -apple-system, sans-serif',
          }}
        >
          &copy; {currentYear} Chemical Database. All rights reserved.
        </Typography>

        {/* Right Section - Company Link */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <Typography
            variant="caption"
            sx={{
              color: '#9CA3AF',
              fontSize: '0.65rem',
              fontWeight: 400,
              fontFamily: '"Inter", "Segoe UI", system-ui, -apple-system, sans-serif',
            }}
          >
            Powered by
          </Typography>
          
          <Tooltip 
            title="Visit e-Neurons Technologies Ltd" 
            arrow
            placement="top"
            PopperProps={{
              sx: {
                '& .MuiTooltip-tooltip': {
                  backgroundColor: '#1A1A1A',
                  color: '#FFFFFF',
                  fontSize: '0.7rem',
                  fontWeight: 500,
                  padding: '6px 14px',
                  borderRadius: '6px',
                  fontFamily: '"Inter", "Segoe UI", system-ui, -apple-system, sans-serif',
                },
                '& .MuiTooltip-arrow': {
                  color: '#1A1A1A',
                },
              },
            }}
          >
            <Link
              href="https://e-neurons.com.ng/"
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.5,
                fontWeight: 600,
                fontSize: '0.7rem',
                color: BRAND_COLORS.primary,
                textDecoration: 'none',
                transition: 'all 0.3s ease',
                position: 'relative',
                fontFamily: '"Inter", "Segoe UI", system-ui, -apple-system, sans-serif',
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  bottom: -1,
                  left: 0,
                  right: 0,
                  height: 1.5,
                  backgroundColor: BRAND_COLORS.primary,
                  transform: 'scaleX(0)',
                  transition: 'transform 0.3s ease',
                  transformOrigin: 'left',
                },
                '&:hover': {
                  color: BRAND_COLORS.primaryDark,
                  transform: 'translateY(-1px)',
                  '&::after': {
                    transform: 'scaleX(1)',
                  },
                  '& .link-icon': {
                    transform: 'translateX(2px)',
                  },
                },
              }}
            >
              e-Neurons Technologies Ltd
              <OpenInNewIcon 
                className="link-icon"
                sx={{ 
                  fontSize: 12, 
                  transition: 'transform 0.3s ease',
                  opacity: 0.6,
                }} 
              />
            </Link>
          </Tooltip>

          <Typography
            variant="caption"
            sx={{
              color: '#D1D5DB',
              fontSize: '0.7rem',
            }}
          >
            |
          </Typography>

          <Typography
            variant="caption"
            sx={{
              color: '#6B7280',
              fontStyle: 'italic',
              fontSize: '0.65rem',
              fontWeight: 400,
              letterSpacing: '0.2px',
              fontFamily: '"Inter", "Segoe UI", system-ui, -apple-system, sans-serif',
            }}
          >
            "Quality Chemicals, Reliable Supply"
          </Typography>
        </Box>
      </Box>

      {/* Mobile Copyright */}
      <Typography
        variant="caption"
        sx={{
          color: '#9CA3AF',
          fontSize: '0.6rem',
          fontWeight: 400,
          letterSpacing: '0.2px',
          display: { xs: 'block', sm: 'none' },
          textAlign: 'center',
          mt: 0.5,
          fontFamily: '"Inter", "Segoe UI", system-ui, -apple-system, sans-serif',
        }}
      >
        &copy; {currentYear} Chemical Database. All rights reserved.
      </Typography>
    </Box>
  );
};

export default Footer;